import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// The namespace, not named imports: an installed kit from before the receive page (29 Oct) has no ReceivePage, and a
// named import of it stopped the whole app loading. With one, /layers draws the page as it was (LegacyLayersChooser).
import * as kitUi from '@flobi/asset-library/ui';
import '@flobi/asset-library/ui.css';
import { tileUrl } from '../editor/content/tileUrl';
import { createFile, listFiles } from '../home/api';
import { relativeWhen, type FileRow } from '../home/rows';
import { useTheme } from '../theme';
import { fetchLayerFile, layerFileAddress, type LayerFile } from './layerFile';
import { LegacyLayersChooser } from './LegacyLayersChooser';
import { FontReceive } from './FontReceive';

/** A row of what arrives, as the kit's ReceivePage lists it (its `ReceiveRow`, kept here while kits differ). */
export interface ReceiveRow { id?: string; name: string; thumbnailUrl?: string | null; color?: string; glyph?: string; tag?: string; muted?: boolean }
type Parcel = { id: string | null; type: string; name: string; from: { app: string | null }; payload: Record<string, unknown> | null };
const kit = kitUi as unknown as {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ReceivePage?: ComponentType<any>;
  useParcel?: () => { parcel: Parcel | null; problem: string | null; loading: boolean };
};
const ReceivePage = kit.ReceivePage;
const noParcel = () => ({ parcel: null, problem: 'This Artwork needs the latest Flobi kit to open what other apps send.', loading: false });
const useParcel = kit.useParcel ?? noParcel;

/**
 * Where a chat's "Open in Artwork" lands (/layers?file=…), Tarek 29 Oct: "in Artwork it asks me: select
 * an existing board or new; when I select or create one, a new artboard is created with the size of the
 * assets and all layers are placed". Redone the same night ("we need a way better UI for this"): the
 * picture large with the layers it brings, as the Layers panel lists them, beside the choice: a new file
 * first, then the person's files as Home shows them. The choice opens the editor on that file with
 * ?layers=…, and the editor places it (EditorPage, placeLayerFile).
 *
 * Since the cross-app pages (29 Oct 2026) this design is the kit's ReceivePage, every app's /receive page, drawn
 * in Artwork's preset; this file supplies what is Artwork's own (the layer file, the files, the opening). The same
 * page answers /receive?parcel=… for a layered picture another app sends (ReceiveRoute).
 */
export function LayersImport() {
  const [params] = useSearchParams();
  return <LayersReceive address={layerFileAddress(params.get('file'))} />;
}

/** /receive?parcel=…: what another Flobi app sends Artwork. A layered picture opens as /layers does. */
export function ReceiveRoute() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { parcel, problem, loading } = useParcel();
  if (parcel?.type === 'layers') {
    // A layer file somewhere in storage (payload.url), or the file inside the parcel: then the parcel's own address,
    // which readLayerFile unwraps — so the editor's ?layers= opens either.
    const url = (parcel.payload as { url?: unknown } | null)?.url;
    const inside = parcel.payload?.file && parcel.id ? `https://storage.flobi.ai/parcels/${parcel.id}.json` : null;
    return <LayersReceive address={layerFileAddress(typeof url === 'string' ? url : inside)} from={parcel.from.app ? { app: parcel.from.app, name: parcel.name } : null} />;
  }
  // Words (a text parcel from Writer, 29 Oct) go the same way, read as text layers on a new artboard (textParcel.ts).
  // A brand's colours (a brand-assets parcel from Brands) too, read as labelled chips on a new artboard (paletteParcel.ts).
  if ((parcel?.type === 'text' || parcel?.type === 'brand-assets') && parcel.id) return <LayersReceive address={layerFileAddress(`https://storage.flobi.ai/parcels/${parcel.id}.json`)} from={parcel.from.app ? { app: parcel.from.app, name: parcel.name } : null} />;
  // A whole board, doc or folder goes the same way too, one artboard per group (wholeParcel.ts).
  if (parcel?.type === 'media' && parcel.payload?.whole && parcel.id) return <LayersReceive address={layerFileAddress(`https://storage.flobi.ai/parcels/${parcel.id}.json`)} from={parcel.from.app ? { app: parcel.from.app, name: parcel.name } : null} />;
  // A font from Flobi Fonts goes into a file's fonts (FontReceive).
  if (parcel?.type === 'font' && ReceivePage) return <FontReceive parcel={parcel} />;
  if (!ReceivePage) return <p className="p-[28px] text-[14px] text-label-2">{problem}</p>;
  return (
    <ReceivePage
      app="artwork"
      mode={theme === 'light' ? 'light' : 'dark'}
      loading={loading}
      problem={problem || (parcel ? "Artwork can't open this kind of thing yet. It takes a picture, a layered picture, words, a font or a brand's colours." : null)}
      onBack={() => navigate('/')}
    />
  );
}

function LayersReceive({ address, from = null }: { address: string | null; from?: { app: string; name: string } | null }) {
  const navigate = useNavigate();
  const [file, setFile] = useState<LayerFile | null>(null);
  const [problem, setProblem] = useState<string | null>(address ? null : 'This link does not point at a layered file from Flobi.');
  const [files, setFiles] = useState<FileRow[] | null>(null);
  /** The choice being opened: 'new', or a file's id. */
  const [going, setGoing] = useState<string | null>(null);

  useEffect(() => {
    if (!address) return;
    let live = true;
    fetchLayerFile(address).then((got) => live && setFile(got), (error: Error) => live && setProblem(error.message));
    return () => { live = false; };
  }, [address]);

  useEffect(() => {
    let live = true;
    listFiles({ limit: 60, scope: 'recent' })
      .then((rows) => live && setFiles(rows))
      .catch(() => live && setFiles([]));
    return () => { live = false; };
  }, []);

  const open = (documentId: string) => navigate(`/f/${encodeURIComponent(documentId)}?layers=${encodeURIComponent(address!)}`, { replace: true });
  const pick = (row: FileRow) => {
    if (!file || going) return;
    setGoing(row.id);
    open(row.id);
  };
  const startNew = async () => {
    if (!file || going) return;
    setGoing('new');
    try {
      // A node's own word ("layerize") or the stand-in is no name for a file.
      const made = await createFile({ name: /^(?:picture|layeri[sz]e|split to layers)$/i.test(file.name) ? 'Layers' : file.name });
      open(made.id);
    } catch (error) {
      setProblem((error as Error).message || 'A new file could not be made. Try again.');
      setGoing(null);
    }
  };

  return <LayersChooser file={file} problem={problem} files={files} going={going} from={from} onNew={startNew} onPick={pick} onBack={() => navigate('/')} />;
}

/** The layers it brings, top first: a picture's own thumbnail, the words for text, a swatch for a flat colour. */
export function layerRows(file: LayerFile): ReceiveRow[] {
  const layers = [...file.document.layers].sort((a, b) => (b.z ?? 0) - (a.z ?? 0));
  return layers.map((layer, index) => {
    const picture = layer.kind === 'image' && typeof layer.result === 'number' ? file.pictures[layer.result] : undefined;
    const name = layer.kind === 'text' ? layer.text?.replace(/\s+/g, ' ').trim() || layer.name || 'Text' : layer.name || picture?.name || `Layer ${layers.length - index}`;
    const hidden = layer.visible === false;
    return {
      id: String(layer.id ?? index),
      name,
      ...(picture ? { thumbnailUrl: tileUrl(picture.url, 80) } : layer.kind === 'fill' ? { color: layer.fill || 'transparent' } : { glyph: layer.kind === 'text' ? 'doc' : 'image' }),
      ...(hidden ? { tag: 'Hidden', muted: true } : layer.kind === 'text' ? { tag: 'Editable' } : {}),
    };
  });
}

/** The page's words for a whole send. */
export function wholeWords(file: LayerFile): { facts: string[]; sentence: string; hint: string } {
  const { pictures, texts, behind, more } = file.whole!;
  const boards = more.length + 1;
  const held = [pictures ? `${pictures} ${pictures === 1 ? 'picture' : 'pictures'}` : '', texts ? `${texts} ${texts === 1 ? 'text' : 'texts'}` : ''].filter(Boolean).join(' · ');
  const where = boards === 1 ? 'It goes on a new artboard' : `It goes on ${boards} new artboards, each group its own`;
  return {
    facts: [held, `${boards} ${boards === 1 ? 'artboard' : 'artboards'}`],
    sentence: `${where}${texts ? ', with the text still editable' : ''}. Nothing is uploaded again and no credits are used.${behind ? ` ${behind} stay behind: Artwork takes pictures and words.` : ''} Choose a new file or one of yours.`,
    hint: `A file of its own, with ${boards === 1 ? 'it on its first artboard' : 'these artboards'}`,
  };
}

/** The page itself, given what it shows: the kit's ReceivePage in Artwork's words and look. */
export function LayersChooser({ file, problem, files, going, from = null, onNew, onPick, onBack }: {
  file: LayerFile | null; problem: string | null; files: FileRow[] | null; going: string | null;
  from?: { app: string; name: string } | null; onNew: () => void; onPick: (row: FileRow) => void; onBack: () => void;
}) {
  const { theme } = useTheme();
  const [query, setQuery] = useState('');
  const byId = useMemo(() => new Map((files ?? []).map((row) => [row.id, row])), [files]);
  const size = file ? `${file.size.width} × ${file.size.height}` : null;
  // One picture (Drive's, a Flow image node's) is a picture, not "the layers".
  const what = file?.count === 1 && !file.texts ? 'The picture goes' : 'The layers go';
  const whole = file?.whole ? { ...wholeWords(file), rows: [file, ...file.whole.more].flatMap((one) => layerRows(one).reverse()) } : null;
  if (!ReceivePage) {
    return <LegacyLayersChooser file={file} problem={problem} files={files} query={query} onQuery={setQuery} going={going} onNew={onNew} onPick={onPick} onBack={onBack} />;
  }
  return (
    <ReceivePage
      app="artwork"
      mode={theme === 'light' ? 'light' : 'dark'}
      title="Open in Artwork"
      from={from}
      loading={!file && !problem}
      problem={problem}
      arriving={file ? { preview: file.preview ? tileUrl(file.preview, 800) : null, facts: whole ? whole.facts : [`${file.count} ${file.count === 1 ? 'layer' : 'layers'}`, size ?? ''], rows: whole ? whole.rows : layerRows(file), glyph: 'image' } : null}
      sentence={whole ? whole.sentence : `${what} on a new artboard ${size ?? 'the size of the picture'}${file?.texts ? ', with the text still editable' : ''}. Choose a new file or one of yours.`}
      newOption={{ label: 'New file', hint: whole ? whole.hint : `A file of its own, with ${what === 'The picture goes' ? 'the picture' : 'the layers'} on its first artboard`, busyLabel: 'Making the file…', onChoose: onNew }}
      existing={{
        heading: 'Add to one of your files',
        searchPlaceholder: 'Search your files',
        chip: 'Add here',
        glyph: 'board',
        loading: files === null,
        empty: 'No files yet. Start a new one above.',
        items: (files ?? []).map((row) => ({ id: row.id, name: row.name || 'Untitled', thumbnailUrl: row.thumbnailUrl ? tileUrl(row.thumbnailUrl, 640) : null, meta: relativeWhen(row.myLastEditAt || row.updatedAt) })),
        onChoose: (item: { id: string }) => {
          const row = byId.get(item.id);
          if (row) onPick(row);
        },
      }}
      going={going}
      onBack={onBack}
      backLabel="Artwork"
    />
  );
}
